/**
 * Event tracker: video (native + iframes), audio.
 * @package AdvancedTrackingManager
 */

import type { AtmConfig } from '@lib/types';
import { createLogger } from '@lib/logger';
import { getPageTitle, sendEvent } from './core';

const w = globalThis as unknown as Window & {
  Vimeo?: {
    Player: new (el: HTMLElement) => {
      on: (ev: string, fn: (data?: { percent?: number }) => void) => void;
      getVideoTitle?: () => Promise<string>;
    };
  };
};

/** Per-iframe state for YouTube postMessage tracking (non-destructive; no YT.Player). */
interface YTPostMessageTracker {
  iframe: HTMLIFrameElement;
  contentWindow: WindowProxy;
  started: boolean;
  progressSent: Record<number, boolean>;
  duration: number;
  currentTime: number;
  videoId: string;
  videoTitleRef: { current: string };
  getPayload: (extra?: Record<string, unknown>) => Record<string, unknown>;
  listeningConfirmed: boolean;
  listeningInterval?: ReturnType<typeof setInterval>;
  config: AtmConfig;
  enable: { videoStart: boolean; videoProgress: boolean; videoComplete: boolean };
}

const ytTrackers = new Map<WindowProxy, YTPostMessageTracker>();
const ytTrackersByIframe = new WeakMap<HTMLIFrameElement, YTPostMessageTracker>();

const YT_ORIGINS = ['https://www.youtube.com', 'https://www.youtube-nocookie.com'] as const;

function isYTOrigin(origin: string): boolean {
  return YT_ORIGINS.includes(origin as (typeof YT_ORIGINS)[number]);
}

/** Selectors matching Swiper carousel containers (Swiper 6+ uses .swiper; legacy uses .swiper-container). */
const SWIPER_SELECTOR = '.swiper, .swiper-container, [class*="swiper"]';

/** Data attribute to mark elements we already attached video tracking to (avoids double-init). */
const ATM_VIDEO_TRACKED = 'atmVideoTracked';

function isInsideSwiper(el: Element): boolean {
  return el.closest(SWIPER_SELECTOR) !== null;
}

function trackSingleNativeVideo(
  video: HTMLVideoElement,
  config: AtmConfig,
  enable: { videoStart: boolean; videoProgress: boolean; videoComplete: boolean }
): void {
  if (Object.hasOwn(video.dataset ?? {}, ATM_VIDEO_TRACKED)) return;
  if (Object.hasOwn(video.dataset ?? {}, 'atmExclude')) return;
  if (isInsideSwiper(video)) return;
  video.dataset[ATM_VIDEO_TRACKED] = '1';

  let started = false;
  const progressSent: Record<number, boolean> = { 25: false, 50: false, 75: false, 100: false };
  const videoUrl =
    (video.currentSrc || video.src || '').trim() ||
    (video.querySelector('source') as HTMLSourceElement)?.src ||
    '';
  const getVideoPayload = (extra?: Record<string, unknown>) => {
    const p: Record<string, unknown> = {
      video_provider: 'native',
      video_url: videoUrl,
      page_title: getPageTitle(),
    };
    const title = video.dataset?.atmVideoTitle?.trim();
    if (title) (p as Record<string, unknown>).video_title = title;
    if (extra) Object.assign(p, extra);
    return p;
  };
  video.addEventListener(
    'play',
    () => {
      if (!started && enable.videoStart) {
        started = true;
        sendEvent('video_start', getVideoPayload(), config);
      }
    },
    true
  );
  video.addEventListener(
    'timeupdate',
    () => {
      if (!video.duration || !enable.videoProgress) return;
      const pct = Math.floor((video.currentTime / video.duration) * 100);
      for (const m of [25, 50, 75, 100]) {
        if (pct >= m && !progressSent[m]) {
          progressSent[m] = true;
          sendEvent('video_progress', getVideoPayload({ progress_percent: m }), config);
        }
      }
    },
    true
  );
  video.addEventListener(
    'ended',
    () => {
      if (enable.videoComplete) sendEvent('video_complete', getVideoPayload(), config);
    },
    true
  );
}

export const trackVideoNative = (
  config: AtmConfig,
  enable: { videoStart: boolean; videoProgress: boolean; videoComplete: boolean }
): void => {
  if (!enable.videoStart && !enable.videoProgress && !enable.videoComplete) return;
  for (const video of document.querySelectorAll<HTMLVideoElement>('video')) {
    trackSingleNativeVideo(video, config, enable);
  }
};

export const trackAudioNative = (
  config: AtmConfig,
  _enable: { videoStart: boolean; videoProgress: boolean; videoComplete: boolean }
): void => {
  for (const audio of document.querySelectorAll<HTMLAudioElement>('audio')) {
    if (Object.hasOwn(audio.dataset ?? {}, 'atmExclude')) continue;
    let started = false;
    const progressSent: Record<number, boolean> = { 25: false, 50: false, 75: false, 100: false };
    const audioUrl =
      (audio.currentSrc || audio.src || '').trim() ||
      (audio.querySelector('source') as HTMLSourceElement)?.src ||
      '';
    const audioId = audio.id || (audio.dataset?.atmAudioId ?? '');
    const getAudioPayload = (extra?: Record<string, unknown>) => {
      const p: Record<string, unknown> = { audio_url: audioUrl, page_title: getPageTitle() };
      if (audioId) (p as Record<string, unknown>).audio_id = audioId;
      if (extra) Object.assign(p, extra);
      return p;
    };
    audio.addEventListener(
      'play',
      () => {
        if (!started) {
          started = true;
          sendEvent('audio_start', getAudioPayload(), config);
        }
      },
      true
    );
    audio.addEventListener(
      'timeupdate',
      () => {
        if (!audio.duration || audio.duration <= 0) return;
        const pct = Math.floor((audio.currentTime / audio.duration) * 100);
        for (const m of [25, 50, 75, 100]) {
          if (pct >= m && !progressSent[m]) {
            progressSent[m] = true;
            sendEvent('audio_progress', getAudioPayload({ progress_percent: m }), config);
          }
        }
      },
      true
    );
    audio.addEventListener(
      'ended',
      () => sendEvent('audio_complete', getAudioPayload(), config),
      true
    );
  }
};

function fetchTitleThenSend(
  tracker: YTPostMessageTracker
): void {
  const { config } = tracker;
  const sendStart = (): void => sendEvent('video_start', tracker.getPayload(), config);
  const endpoint = config.videoTitleEndpoint;
  if (endpoint && tracker.videoId) {
    fetch(`${endpoint}?provider=youtube&id=${encodeURIComponent(tracker.videoId)}`)
      .then((r) => r.json())
      .then((j: { title?: string }) => {
        if (j?.title) tracker.videoTitleRef.current = j.title;
      })
      .catch(() => {})
      .finally(sendStart);
  } else {
    sendStart();
  }
}

let ytMessageListenerAttached = false;

function attachYTMessageListener(): void {
  if (ytMessageListenerAttached) return;
  ytMessageListenerAttached = true;
  window.addEventListener('message', (event: MessageEvent) => {
    if (!isYTOrigin(event.origin)) return;
    let data: { event?: string; info?: unknown };
    try {
      data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
    } catch {
      return;
    }
    const tracker = ytTrackers.get(event.source as WindowProxy);
    if (!tracker) return;
    tracker.listeningConfirmed = true;
    const { config, enable } = tracker;

    if (data.event === 'infoDelivery' && data.info) {
      const info = data.info as {
        currentTime?: number;
        duration?: number;
        playerState?: number;
      };
      if (info.duration != null && info.duration > 0) tracker.duration = info.duration;
      if (typeof info.currentTime === 'number') tracker.currentTime = info.currentTime;

      if (tracker.duration > 0 && tracker.started && enable.videoProgress) {
        const pct = Math.floor((tracker.currentTime / tracker.duration) * 100);
        for (const m of [25, 50, 75, 100]) {
          if (pct >= m && !tracker.progressSent[m]) {
            tracker.progressSent[m] = true;
            sendEvent('video_progress', tracker.getPayload({ progress_percent: m }), config);
          }
        }
      }
    }

    if (data.event === 'onStateChange') {
      const state = data.info as number;
      if (state === 1 && !tracker.started && enable.videoStart) {
        tracker.started = true;
        fetchTitleThenSend(tracker);
      }
      if (state === 2 && tracker.started) {
        const pct =
          tracker.duration > 0
            ? Math.floor((tracker.currentTime / tracker.duration) * 100)
            : 0;
        sendEvent('video_pause', tracker.getPayload({ progress_percent: pct }), config);
      }
      if (state === 0) {
        if (enable.videoProgress && !tracker.progressSent[100]) {
          tracker.progressSent[100] = true;
          sendEvent('video_progress', tracker.getPayload({ progress_percent: 100 }), config);
        }
        if (enable.videoComplete) {
          sendEvent('video_complete', tracker.getPayload(), config);
        }
      }
    }
  });
}

/** Selector for YouTube/Vimeo iframes (used for initial scan and observer). */
const VIDEO_IFRAME_SELECTOR =
  'iframe[src*="youtube"], iframe[src*="youtu.be"], iframe[src*="youtube-nocookie"], iframe[src*="vimeo.com"], iframe[src*="player.vimeo.com"]';

function trackSingleVideoIframe(
  iframe: HTMLIFrameElement,
  config: AtmConfig,
  enable: { videoStart: boolean; videoProgress: boolean; videoComplete: boolean },
  log: ReturnType<typeof createLogger>
): void {
  if (Object.hasOwn(iframe.dataset ?? {}, ATM_VIDEO_TRACKED)) return;
  if (Object.hasOwn(iframe.dataset ?? {}, 'atmExclude')) return;
  if (isInsideSwiper(iframe)) return;
  iframe.dataset[ATM_VIDEO_TRACKED] = '1';

  const src = (iframe.src ?? '').trim();
  let provider = 'native';
  let videoId = '';
  let videoUrl = src;
  if (src.includes('youtube') || src.includes('youtu.be')) {
    provider = 'youtube';
    try {
      const u = new URL(src);
      const pathSegments = u.pathname.replace(/^\//, '').split('/').filter(Boolean);
      if (u.searchParams.has('v')) {
        videoId = u.searchParams.get('v') ?? '';
      } else if (pathSegments[0] === 'embed' && pathSegments[1]) {
        videoId = pathSegments[1].split('?')[0];
      } else if (u.hostname === 'youtu.be' && pathSegments[0]) {
        videoId = pathSegments[0].split('?')[0];
      } else {
        videoId = pathSegments[0]?.split('?')[0] ?? '';
      }
      if (videoId === 'embed' || !/^[\w-]{10,20}$/.test(videoId)) {
        videoId = '';
      }
      videoUrl = videoId ? `https://www.youtube.com/watch?v=${videoId}` : src;
    } catch {
      // invalid URL
    }
  } else if (src.includes('vimeo')) {
    provider = 'vimeo';
    try {
      const vimeoUrl = new URL(src);
      videoId = vimeoUrl.pathname.replace(/^\//, '').split('/')[0] ?? '';
      videoUrl = videoId ? `https://vimeo.com/${videoId}` : src;
    } catch {
      // invalid URL
    }
  }
  const progressSent: Record<number, boolean> = { 25: false, 50: false, 75: false, 100: false };
  const videoTitleRef = { current: '' };
  const getPayload = (extra?: Record<string, unknown>) => {
    const p: Record<string, unknown> = {
      video_provider: provider,
      video_url: videoUrl,
      page_title: getPageTitle(),
    };
    if (videoId) (p as Record<string, unknown>).video_id = videoId;
    if (videoTitleRef.current) (p as Record<string, unknown>).video_title = videoTitleRef.current;
    if (extra) Object.assign(p, extra);
    return p;
  };
  if (provider === 'youtube') {
    try {
      const url = new URL(iframe.src);
      const alreadyHasApi = url.searchParams.has('enablejsapi');
      if (!alreadyHasApi) {
        url.searchParams.set('enablejsapi', '1');
        if (!url.searchParams.has('origin')) {
          url.searchParams.set('origin', window.location.origin);
        }
        iframe.src = url.toString();
      }
    } catch {
      return;
    }

    const contentWindow = iframe.contentWindow;
    if (!contentWindow) return;

    const tracker: YTPostMessageTracker = {
      iframe,
      contentWindow,
      started: false,
      progressSent: { ...progressSent },
      duration: 0,
      currentTime: 0,
      videoId,
      videoTitleRef,
      getPayload,
      listeningConfirmed: false,
      config,
      enable,
    };
    ytTrackers.set(contentWindow as WindowProxy, tracker);
    ytTrackersByIframe.set(iframe, tracker);
    attachYTMessageListener();

    const startListening = (): void => {
      const msg = JSON.stringify({ event: 'listening', id: 1, channel: 'widget' });
      try {
        // Use '*' here to avoid origin mismatch errors while still
        // validating incoming messages via isYTOrigin(event.origin).
        iframe.contentWindow?.postMessage(msg, '*');
      } catch {
        // ignore
      }
      try {
        iframe.contentWindow?.postMessage(msg, '*');
      } catch {
        // ignore
      }
    };

    const startRetryInterval = (): void => {
      if (tracker.listeningInterval !== undefined) return;
      tracker.listeningInterval = setInterval(() => {
        if (tracker.listeningConfirmed) {
          if (tracker.listeningInterval !== undefined) {
            clearInterval(tracker.listeningInterval);
            tracker.listeningInterval = undefined;
          }
          return;
        }
        startListening();
      }, 1000);
      setTimeout(() => {
        if (tracker.listeningInterval !== undefined) {
          clearInterval(tracker.listeningInterval);
          tracker.listeningInterval = undefined;
        }
      }, 30000);
    };

    // Only postMessage after iframe has loaded; otherwise target origin is wrong (e.g. eyfs.ie) and browser throws.
    const onIframeLoaded = (): void => {
      startListening();
      startRetryInterval();
    };
    iframe.addEventListener('load', onIframeLoaded, { once: true });
    // Early one-off attempt for already-loaded modal iframes (theme may append iframe after load); avoids missing first PLAYING.
    setTimeout(() => {
      if (!tracker.listeningConfirmed) startListening();
    }, 50);
    // If load still didn't fire (e.g. iframe was in DOM before we attached), run full handshake + retry so we subscribe.
    setTimeout(() => {
      if (!tracker.listeningConfirmed) {
        onIframeLoaded();
      }
    }, 250);
    return;
  }
  if (provider === 'vimeo' && typeof w.Vimeo !== 'undefined' && w.Vimeo.Player) {
    let started = false;
    try {
      const vimeoPlayer = new w.Vimeo.Player(iframe);
      vimeoPlayer.on('play', () => {
        if (!started && enable.videoStart) {
          started = true;
          const sendStart = (): void => sendEvent('video_start', getPayload(), config);
          if (typeof vimeoPlayer.getVideoTitle === 'function') {
            vimeoPlayer
              .getVideoTitle()
              .then((title: string) => {
                if (title) videoTitleRef.current = title;
              })
              .catch(() => {})
              .finally(sendStart);
          } else {
            sendStart();
          }
        }
      });
      vimeoPlayer.on('timeupdate', (data?: { percent?: number }) => {
        if (!enable.videoProgress) return;
        const pct = data?.percent ? Math.floor(data.percent * 100) : 0;
        for (const m of [25, 50, 75, 100]) {
          if (pct >= m && !progressSent[m]) {
            progressSent[m] = true;
            sendEvent('video_progress', getPayload({ progress_percent: m }), config);
          }
        }
      });
      vimeoPlayer.on('ended', () => {
        if (enable.videoComplete) sendEvent('video_complete', getPayload(), config);
      });
    } catch (err) {
      log.debug('Vimeo player init failed', err);
    }
  }
}

export const trackVideoIframes = (
  config: AtmConfig,
  enable: { videoStart: boolean; videoProgress: boolean; videoComplete: boolean }
): void => {
  if (!enable.videoStart && !enable.videoProgress && !enable.videoComplete) return;
  const log = createLogger('ATM Event Tracker', config.debugMode);
  const iframes = document.querySelectorAll<HTMLIFrameElement>(VIDEO_IFRAME_SELECTOR);
  for (const iframe of iframes) {
    trackSingleVideoIframe(iframe, config, enable, log);
  }
};

/**
 * Observe the DOM for dynamically added YouTube/Vimeo iframes and native videos,
 * and attach tracking so modal/lazy-loaded players send video_start, video_progress, video_complete.
 */
export const observeDynamicVideoElements = (
  config: AtmConfig,
  enable: { videoStart: boolean; videoProgress: boolean; videoComplete: boolean }
): void => {
  if (!enable.videoStart && !enable.videoProgress && !enable.videoComplete) return;
  const log = createLogger('ATM Event Tracker', config.debugMode);

  const deferredTrackIframe = (iframe: HTMLIFrameElement): void => {
    setTimeout(() => {
      if (
        iframe.isConnected &&
        iframe.matches(VIDEO_IFRAME_SELECTOR) &&
        !Object.hasOwn(iframe.dataset ?? {}, ATM_VIDEO_TRACKED)
      ) {
        // eslint-disable-next-line no-console
        console.log('[ATM Video observe] Deferred iframe now matches selector, attaching tracking', { src: iframe.src });
        trackSingleVideoIframe(iframe, config, enable, log);
      }
    }, 500);
  };

  const processNode = (node: Node): void => {
    if (node.nodeType !== Node.ELEMENT_NODE || !node.isConnected) return;
    const el = node as Element;

    if (el.tagName === 'IFRAME') {
      const iframeEl = el as HTMLIFrameElement;
      if (el.matches(VIDEO_IFRAME_SELECTOR)) {
        trackSingleVideoIframe(iframeEl, config, enable, log);
      } else {
        deferredTrackIframe(iframeEl);
      }
    }
    for (const iframe of el.querySelectorAll<HTMLIFrameElement>('iframe')) {
      if (Object.hasOwn(iframe.dataset ?? {}, ATM_VIDEO_TRACKED)) continue;
      if (iframe.matches(VIDEO_IFRAME_SELECTOR)) {
        trackSingleVideoIframe(iframe, config, enable, log);
      } else {
        deferredTrackIframe(iframe);
      }
    }

    if (el.tagName === 'VIDEO') {
      trackSingleNativeVideo(el as HTMLVideoElement, config, enable);
    }
    for (const video of el.querySelectorAll<HTMLVideoElement>('video')) {
      if (!Object.hasOwn(video.dataset ?? {}, ATM_VIDEO_TRACKED)) {
        trackSingleNativeVideo(video, config, enable);
      }
    }
  };

  const handleIframeRemoved = (iframe: HTMLIFrameElement): void => {
    const tracker = ytTrackersByIframe.get(iframe);
    if (!tracker) return;
    const { config, enable } = tracker;
    if (!enable.videoStart) return;
    if (!tracker.started) return;
    const pct =
      tracker.duration > 0
        ? Math.floor((tracker.currentTime / tracker.duration) * 100)
        : 0;
    sendEvent(
      'video_pause',
      tracker.getPayload({ progress_percent: pct, pause_reason: 'iframe_removed' }),
      config
    );
    ytTrackers.delete(tracker.contentWindow);
    ytTrackersByIframe.delete(iframe);
  };

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        processNode(node);
      }
      for (const removed of mutation.removedNodes) {
        if (removed.nodeType !== Node.ELEMENT_NODE) continue;
        const el = removed as Element;
        if (el.tagName === 'IFRAME') {
          handleIframeRemoved(el as HTMLIFrameElement);
        }
        for (const iframe of el.querySelectorAll<HTMLIFrameElement>('iframe')) {
          handleIframeRemoved(iframe);
        }
      }
      if (mutation.type === 'attributes' && mutation.attributeName === 'src') {
        const target = mutation.target as Element;
        if (target.tagName === 'IFRAME') {
          const iframeTarget = target as HTMLIFrameElement;
          if (
            target.matches(VIDEO_IFRAME_SELECTOR) &&
            !Object.hasOwn(iframeTarget.dataset ?? {}, ATM_VIDEO_TRACKED)
          ) {
            trackSingleVideoIframe(iframeTarget, config, enable, log);
          }
        }
      }
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['src'],
  });
};
